import React, { useState, useEffect } from "react";
import { AlertTriangle, Save, ToggleLeft, ToggleRight, Clock, DollarSign, Info, Calculator, Building2, Timer } from "lucide-react";
import { SuperAdminApis } from '../../apis/superAdminApi';
import AdminSidebar from "../Sidebar/AdminSidebar";
import AdminNavbar from "../Navbars/AdminNavbar";

interface FineSettingsData {
    base_fine_rate: number;
    grace_period_hours: number;
    is_enabled: boolean;
    facility_base_fine_rate: number;
    facility_grace_period_hours: number;
    facility_fine_enabled: boolean;
    auto_attempt_enabled: boolean;
    auto_attempt_hours: number;
    auto_attempt_minutes: number;
    auto_attempt_fine_amount: number;
    auto_attempt_scope: string;
    last_updated_by_name?: string;
    updated_at?: string;
}

const FineSettings: React.FC = () => {
    const [settings, setSettings] = useState<FineSettingsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [savingOfd, setSavingOfd] = useState(false);
    const [savingFacility, setSavingFacility] = useState(false);
    const [togglingOfd, setTogglingOfd] = useState(false);
    const [togglingFacility, setTogglingFacility] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // OFD form
    const [baseFineRate, setBaseFineRate] = useState<string>("");
    const [gracePeriodHours, setGracePeriodHours] = useState<string>("");

    // Facility form
    const [facilityFineRate, setFacilityFineRate] = useState<string>("");
    const [facilityGracePeriodHours, setFacilityGracePeriodHours] = useState<string>("");

    // Auto-attempt automation form
    const [savingAutoAttempt, setSavingAutoAttempt] = useState(false);
    const [togglingAutoAttempt, setTogglingAutoAttempt] = useState(false);
    const [autoAttemptHours, setAutoAttemptHours] = useState<string>("");
    const [autoAttemptMinutes, setAutoAttemptMinutes] = useState<string>("");
    const [autoAttemptFineAmount, setAutoAttemptFineAmount] = useState<string>("");
    const [autoAttemptScope, setAutoAttemptScope] = useState<string>("shein_only");

    // Calculator
    const [calcTab, setCalcTab] = useState<'ofd' | 'facility'>('ofd');
    const [exampleRate, setExampleRate] = useState<string>("1000");
    const [exampleHoursLate, setExampleHoursLate] = useState<string>("50");

    useEffect(() => { fetchSettings(); }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await SuperAdminApis.getFineSettings();
            if (response.success) {
                setSettings(response.data);
                setBaseFineRate(String(response.data.base_fine_rate));
                setGracePeriodHours(String(response.data.grace_period_hours));
                setFacilityFineRate(String(response.data.facility_base_fine_rate ?? 50));
                setFacilityGracePeriodHours(String(response.data.facility_grace_period_hours ?? 72));
                setAutoAttemptHours(String(response.data.auto_attempt_hours ?? 23));
                setAutoAttemptMinutes(String(response.data.auto_attempt_minutes ?? 40));
                setAutoAttemptFineAmount(String(response.data.auto_attempt_fine_amount ?? 50));
                setAutoAttemptScope(response.data.auto_attempt_scope ?? "shein_only");
            }
        } catch (err: any) { setError("Failed to load fine settings."); }
        finally { setLoading(false); }
    };

    const clearMessages = () => { setError(""); setSuccess(""); };

    const handleSaveOfd = async () => {
        clearMessages();
        const rate = parseFloat(baseFineRate);
        const hours = parseInt(gracePeriodHours);
        if (isNaN(rate) || rate < 0) { setError("OFD base fine rate must be a positive number."); return; }
        if (isNaN(hours) || hours < 1) { setError("OFD grace period must be at least 1 hour."); return; }
        try {
            setSavingOfd(true);
            const response = await SuperAdminApis.updateFineSettings({ base_fine_rate: rate, grace_period_hours: hours });
            if (response.success) {
                setSuccess(response.message || "OFD fine settings updated.");
                setSettings(prev => prev ? { ...prev, base_fine_rate: rate, grace_period_hours: hours } : prev);
            } else { setError(response.message || "Failed to update."); }
        } catch (err: any) { setError(err?.response?.data?.message || "Failed to update OFD settings."); }
        finally { setSavingOfd(false); }
    };

    const handleSaveFacility = async () => {
        clearMessages();
        const rate = parseFloat(facilityFineRate);
        const hours = parseInt(facilityGracePeriodHours);
        if (isNaN(rate) || rate < 0) { setError("Facility fine rate must be a positive number."); return; }
        if (isNaN(hours) || hours < 1) { setError("Facility grace period must be at least 1 hour."); return; }
        try {
            setSavingFacility(true);
            const response = await SuperAdminApis.updateFacilityFineSettings({ facility_base_fine_rate: rate, facility_grace_period_hours: hours });
            if (response.success) {
                setSuccess(response.message || "Facility fine settings updated.");
                setSettings(prev => prev ? { ...prev, facility_base_fine_rate: rate, facility_grace_period_hours: hours } : prev);
            } else { setError(response.message || "Failed to update."); }
        } catch (err: any) { setError(err?.response?.data?.message || "Failed to update facility settings."); }
        finally { setSavingFacility(false); }
    };

    const handleToggleOfd = async () => {
        clearMessages();
        try {
            setTogglingOfd(true);
            const response = await SuperAdminApis.toggleFineSettings();
            if (response.success) { setSuccess(response.message); setSettings(prev => prev ? { ...prev, is_enabled: response.data.is_enabled } : prev); }
            else { setError(response.message || "Failed to toggle."); }
        } catch (err: any) { setError(err?.response?.data?.message || "Failed to toggle OFD fines."); }
        finally { setTogglingOfd(false); }
    };

    const handleToggleFacility = async () => {
        clearMessages();
        try {
            setTogglingFacility(true);
            const response = await SuperAdminApis.toggleFacilityFineSettings();
            if (response.success) { setSuccess(response.message); setSettings(prev => prev ? { ...prev, facility_fine_enabled: response.data.facility_fine_enabled } : prev); }
            else { setError(response.message || "Failed to toggle."); }
        } catch (err: any) { setError(err?.response?.data?.message || "Failed to toggle facility fines."); }
        finally { setTogglingFacility(false); }
    };

    const handleSaveAutoAttempt = async () => {
        clearMessages();
        const hours = parseInt(autoAttemptHours);
        const minutes = parseInt(autoAttemptMinutes);
        const fine = parseFloat(autoAttemptFineAmount);
        if (isNaN(hours) || hours < 0 || hours > 72) { setError("Threshold hours must be between 0 and 72."); return; }
        if (isNaN(minutes) || minutes < 0 || minutes > 59) { setError("Threshold minutes must be between 0 and 59."); return; }
        if (hours * 60 + minutes < 1) { setError("Threshold must be greater than zero."); return; }
        if (isNaN(fine) || fine < 0) { setError("Auto-attempt fine must be a positive number."); return; }
        try {
            setSavingAutoAttempt(true);
            const response = await SuperAdminApis.updateAutoAttemptSettings({
                auto_attempt_hours: hours,
                auto_attempt_minutes: minutes,
                auto_attempt_fine_amount: fine,
                auto_attempt_scope: autoAttemptScope,
            });
            if (response.success) {
                setSuccess(response.message || "Auto-attempt settings updated.");
                setSettings(prev => prev ? {
                    ...prev,
                    auto_attempt_hours: hours,
                    auto_attempt_minutes: minutes,
                    auto_attempt_fine_amount: fine,
                    auto_attempt_scope: autoAttemptScope,
                } : prev);
            } else { setError(response.message || "Failed to update."); }
        } catch (err: any) { setError(err?.response?.data?.message || "Failed to update auto-attempt settings."); }
        finally { setSavingAutoAttempt(false); }
    };

    const handleToggleAutoAttempt = async () => {
        clearMessages();
        try {
            setTogglingAutoAttempt(true);
            const response = await SuperAdminApis.toggleAutoAttemptSettings();
            if (response.success) {
                setSuccess(response.message);
                setSettings(prev => prev ? { ...prev, auto_attempt_enabled: response.data.auto_attempt_enabled } : prev);
            } else { setError(response.message || "Failed to toggle."); }
        } catch (err: any) { setError(err?.response?.data?.message || "Failed to toggle auto-attempt automation."); }
        finally { setTogglingAutoAttempt(false); }
    };

    const calculateExample = () => {
        const rate = parseFloat(exampleRate) || 0;
        const totalHours = parseFloat(exampleHoursLate) || 0;
        const grace = calcTab === 'ofd' ? (parseFloat(gracePeriodHours) || 24) : (parseFloat(facilityGracePeriodHours) || 72);
        const fineRate = calcTab === 'ofd' ? (parseFloat(baseFineRate) || 50) : (parseFloat(facilityFineRate) || 50);
        if (totalHours <= grace) return { fine: 0, net: rate, days: 0, hoursLate: 0 };
        const hoursLate = totalHours - grace;
        const days = Math.ceil(hoursLate / 24);
        const rawFine = fineRate * days;
        const fine = Math.min(rawFine, rate);
        return { fine, net: rate - fine, days, hoursLate: Math.round(hoursLate * 100) / 100 };
    };

    const example = calculateExample();

    if (loading) {
        return (
            <>
                <AdminSidebar title="Fine Settings" />
                <div className="relative md:ml-64 bg-[#FAFAFA]">
                    <AdminNavbar title="Fine Settings" />
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-gray-600">Loading fine settings...</span>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <AdminSidebar title="Fine Settings" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Fine Settings" />
                <div className="p-6 max-w-5xl mx-auto">
                    {/* Header */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <AlertTriangle className="w-7 h-7 text-orange-500" />
                            Delivery Fine Settings
                        </h1>
                        <p className="text-gray-500 mt-1">Configure automatic fines for late deliveries and facility overstay. Fines are deducted from the rider/fleet delivery commission.</p>
                    </div>

                    {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
                    {success && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{success}</div>}

                    {/* ══════════════════════════════════════════════════ */}
                    {/* SECTION 1: OFD LATE DELIVERY FINES                */}
                    {/* ══════════════════════════════════════════════════ */}
                    <div className="mb-8">
                        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-blue-500" />
                            OFD Late Delivery Fines
                        </h2>

                        {/* Toggle */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">Automatic OFD Fines</h3>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {settings?.is_enabled ? "Active — riders are fined when deliveries exceed the grace period after Out for Delivery." : "Disabled — no OFD fines are being applied."}
                                    </p>
                                </div>
                                <button onClick={handleToggleOfd} disabled={togglingOfd}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${settings?.is_enabled ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                                    {togglingOfd ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                        : settings?.is_enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                                    {settings?.is_enabled ? "Enabled" : "Disabled"}
                                </button>
                            </div>
                        </div>

                        {/* Config */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Fine Rate (₦ per shipment per day)</label>
                                    <input type="number" value={baseFineRate} onChange={e => setBaseFineRate(e.target.value)} min="0" step="0.01"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" placeholder="e.g. 50" />
                                    <p className="text-xs text-gray-400 mt-1">Amount charged per shipment for each day late after grace period.</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1"><Clock className="w-4 h-4 inline mr-1" />Grace Period (hours)</label>
                                    <input type="number" value={gracePeriodHours} onChange={e => setGracePeriodHours(e.target.value)} min="1" step="1"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" placeholder="e.g. 24" />
                                    <p className="text-xs text-gray-400 mt-1">Hours after OFD before fines start. Typically 24 hours.</p>
                                </div>
                            </div>
                            <button onClick={handleSaveOfd} disabled={savingOfd}
                                className="mt-4 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm">
                                {savingOfd ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Save className="w-4 h-4" />}
                                {savingOfd ? "Saving..." : "Save OFD Settings"}
                            </button>
                        </div>
                    </div>

                    {/* ══════════════════════════════════════════════════ */}
                    {/* SECTION 2: FACILITY OVERSTAY FINES                */}
                    {/* ══════════════════════════════════════════════════ */}
                    <div className="mb-8">
                        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-purple-500" />
                            Facility Overstay Fines
                        </h2>

                        {/* Toggle */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">Automatic Facility Fines</h3>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {settings?.facility_fine_enabled
                                            ? "Active — fleets are fined when shipments sit at 'Console Arrived at Facility' beyond the grace period."
                                            : "Disabled — no facility overstay fines are being applied."}
                                    </p>
                                </div>
                                <button onClick={handleToggleFacility} disabled={togglingFacility}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${settings?.facility_fine_enabled ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                                    {togglingFacility ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                        : settings?.facility_fine_enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                                    {settings?.facility_fine_enabled ? "Enabled" : "Disabled"}
                                </button>
                            </div>
                        </div>

                        {/* Config */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Facility Fine Rate (₦ per shipment per day)</label>
                                    <input type="number" value={facilityFineRate} onChange={e => setFacilityFineRate(e.target.value)} min="0" step="0.01"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm" placeholder="e.g. 50" />
                                    <p className="text-xs text-gray-400 mt-1">Amount charged per shipment for each day at facility past grace period.</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1"><Clock className="w-4 h-4 inline mr-1" />Grace Period (hours)</label>
                                    <input type="number" value={facilityGracePeriodHours} onChange={e => setFacilityGracePeriodHours(e.target.value)} min="1" step="1"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm" placeholder="e.g. 72" />
                                    <p className="text-xs text-gray-400 mt-1">Hours at facility before fines start. Typically 72 hours (3 days).</p>
                                </div>
                            </div>
                            <button onClick={handleSaveFacility} disabled={savingFacility}
                                className="mt-4 flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium text-sm">
                                {savingFacility ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Save className="w-4 h-4" />}
                                {savingFacility ? "Saving..." : "Save Facility Settings"}
                            </button>
                        </div>
                    </div>

                    {/* ══════════════════════════════════════════════════ */}
                    {/* SECTION 3: AUTO-ATTEMPT AUTOMATION (SLA)          */}
                    {/* ══════════════════════════════════════════════════ */}
                    <div className="mb-8">
                        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                            <Timer className="w-5 h-5 text-orange-500" />
                            Auto-Attempt Automation (Partner SLA Protection)
                        </h2>

                        {/* Toggle */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">Auto-Post Attempted Delivery</h3>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {settings?.auto_attempt_enabled
                                            ? `Active — shipments still Out for Delivery after ${autoAttemptHours || 23}h ${autoAttemptMinutes || 40}m are automatically posted to Attempted Delivery and fined.`
                                            : "Disabled — no shipments are being auto-posted. We remain exposed to partner SLA charges."}
                                    </p>
                                </div>
                                <button onClick={handleToggleAutoAttempt} disabled={togglingAutoAttempt}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${settings?.auto_attempt_enabled ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                                    {togglingAutoAttempt ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                        : settings?.auto_attempt_enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                                    {settings?.auto_attempt_enabled ? "Enabled" : "Disabled"}
                                </button>
                            </div>
                        </div>

                        {/* Config */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1"><Clock className="w-4 h-4 inline mr-1" />Threshold Hours</label>
                                    <input type="number" value={autoAttemptHours} onChange={e => setAutoAttemptHours(e.target.value)} min="0" max="72" step="1"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm" placeholder="23" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Threshold Minutes</label>
                                    <input type="number" value={autoAttemptMinutes} onChange={e => setAutoAttemptMinutes(e.target.value)} min="0" max="59" step="1"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm" placeholder="40" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Fine per Shipment (₦)</label>
                                    <input type="number" value={autoAttemptFineAmount} onChange={e => setAutoAttemptFineAmount(e.target.value)} min="0" step="0.01"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm" placeholder="50" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Applies To</label>
                                    <select value={autoAttemptScope} onChange={e => setAutoAttemptScope(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm">
                                        <option value="shein_only">Shein shipments only</option>
                                        <option value="all">All shipments</option>
                                    </select>
                                </div>
                            </div>

                            <p className="text-xs text-gray-400 mt-3">
                                Clock starts at Out for Delivery. Nothing is ever auto-attempted at or past <strong>23h 55m</strong>,
                                so the gap on tracking always stays visibly under 24 hours. Shipments older than that are left
                                alone — the deadline has gone, so acting would fine the rider for nothing. Default 23h 40m leaves
                                a 15-minute window, so the sweep must run every ~10 minutes to catch shipments inside it.
                            </p>

                            <button onClick={handleSaveAutoAttempt} disabled={savingAutoAttempt}
                                className="mt-4 flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium text-sm">
                                {savingAutoAttempt ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Save className="w-4 h-4" />}
                                {savingAutoAttempt ? "Saving..." : "Save Auto-Attempt Settings"}
                            </button>
                        </div>

                        <div className="bg-orange-50 rounded-lg border border-orange-200 p-5 mt-4">
                            <h3 className="text-sm font-semibold text-orange-800 mb-3 flex items-center gap-2"><Info className="w-4 h-4" />How Auto-Attempt Works</h3>
                            <ul className="text-sm text-orange-700 space-y-1.5">
                                <li><strong>1.</strong> Shipment goes Out for Delivery — the SLA clock starts.</li>
                                <li><strong>2.</strong> At <strong>{autoAttemptHours || 23}h {autoAttemptMinutes || 40}m</strong> with no POD, the system posts <strong>Attempted Delivery — No One Available/Home</strong> to FFDX with a randomised remark.</li>
                                <li><strong>2b.</strong> Hard ceiling at <strong>23h 55m</strong> — nothing is ever auto-attempted at or past that, on either clock, so tracking never shows a 24-hour gap. Older shipments are skipped entirely and no fine is raised.</li>
                                <li><strong>3.</strong> The rider (or their fleet) is fined a flat <strong>₦{autoAttemptFineAmount || 50}</strong> per shipment.</li>
                                <li><strong>4.</strong> Unlike the OFD fine, this is <strong>not capped</strong> at the commission — it can exceed what the rider earns on that delivery.</li>
                                <li><strong>5.</strong> The fine is recovered from the rider's next approved delivery payment. It never pushes a wallet negative; any remainder carries forward.</li>
                                <li><strong>6.</strong> 24 hours later the shipment auto-returns to Scheduled for Delivery and re-enters the queue.</li>
                                <li><strong>7.</strong> If it goes late again on the next cycle, it is fined again — fines accrue per cycle.</li>
                                <li><strong>8.</strong> Every auto-posted shipment appears on the <strong>Auto-Attempted Deliveries</strong> page for customer service to treat.</li>
                            </ul>
                        </div>
                    </div>

                    {/* ══════════════════════════════════════════════════ */}
                    {/* HOW IT WORKS + CALCULATOR                         */}
                    {/* ══════════════════════════════════════════════════ */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* How It Works */}
                        <div className="space-y-4">
                            <div className="bg-blue-50 rounded-lg border border-blue-200 p-5">
                                <h3 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2"><Info className="w-4 h-4" />How OFD Fines Work</h3>
                                <ul className="text-sm text-blue-700 space-y-1.5">
                                    <li><strong>1.</strong> Rider creates Out for Delivery (OFD) — clock starts.</li>
                                    <li><strong>2.</strong> Rider has <strong>{gracePeriodHours || "24"} hours</strong> to deliver.</li>
                                    <li><strong>3.</strong> After grace period, fines accumulate at <strong>₦{baseFineRate || "50"}/day</strong>.</li>
                                    <li><strong>4.</strong> Late days counted in 24-hour blocks (rounded up).</li>
                                    <li><strong>5.</strong> Fine deducted from commission. Minimum earning = ₦0.</li>
                                    <li><strong>6.</strong> Fleet riders — fine goes to fleet's wallet.</li>
                                    <li><strong>7.</strong> Failed delivery / return = no commission, no fine.</li>
                                    <li><strong>8.</strong> Manual fines (admin-applied) still work independently.</li>
                                </ul>
                            </div>

                            <div className="bg-purple-50 rounded-lg border border-purple-200 p-5">
                                <h3 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4" />How Facility Fines Work</h3>
                                <ul className="text-sm text-purple-700 space-y-1.5">
                                    <li><strong>1.</strong> Shipment arrives at facility ("Console Arrived at Facility") — clock starts.</li>
                                    <li><strong>2.</strong> Fleet has <strong>{facilityGracePeriodHours || "72"} hours</strong> to move the shipment out.</li>
                                    <li><strong>3.</strong> After grace period, fines accumulate at <strong>₦{facilityFineRate || "50"}/day</strong>.</li>
                                    <li><strong>4.</strong> Fine charged to the fleet attached to the shipment's manifest.</li>
                                    <li><strong>5.</strong> Fine deducted from delivery commission at POD time.</li>
                                    <li><strong>6.</strong> Both OFD + facility fines can apply simultaneously.</li>
                                    <li><strong>7.</strong> Total of both fines is capped at gross commission (minimum earning = ₦0).</li>
                                </ul>
                            </div>
                        </div>

                        {/* Calculator */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                            <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2"><Calculator className="w-4 h-4 text-purple-500" />Live Example Calculator</h3>

                            <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1">
                                <button onClick={() => { setCalcTab('ofd'); setExampleHoursLate('50'); }}
                                    className={`flex-1 px-3 py-1.5 rounded text-xs font-semibold transition ${calcTab === 'ofd' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`}>
                                    OFD Fine
                                </button>
                                <button onClick={() => { setCalcTab('facility'); setExampleHoursLate('96'); }}
                                    className={`flex-1 px-3 py-1.5 rounded text-xs font-semibold transition ${calcTab === 'facility' ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`}>
                                    Facility Fine
                                </button>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Rate (₦)</label>
                                    <input type="number" value={exampleRate} onChange={e => setExampleRate(e.target.value)} className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">{calcTab === 'ofd' ? 'Hours from OFD to POD' : 'Hours at facility'}</label>
                                    <input type="number" value={exampleHoursLate} onChange={e => setExampleHoursLate(e.target.value)} className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm" />
                                </div>
                            </div>

                            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                {example.days === 0 ? (
                                    <p className="text-sm text-green-700 font-medium">
                                        ✓ {calcTab === 'ofd' ? 'Delivered' : 'Moved out'} within grace period — no fine.
                                        <br /><span className="text-green-600">Rider/Fleet earns full ₦{Number(exampleRate || 0).toLocaleString()}</span>
                                    </p>
                                ) : (
                                    <div className="text-sm space-y-1">
                                        <p className="text-red-700 font-medium">✗ {calcTab === 'ofd' ? 'Late' : 'Overstay'} by {example.days} {example.days === 1 ? "day" : "days"}</p>
                                        <p className="text-gray-600">Hours past grace: {example.hoursLate}hrs → {example.days} day{example.days !== 1 ? "s" : ""} (rounded up)</p>
                                        <p className="text-gray-600">
                                            Fine: ₦{calcTab === 'ofd' ? (baseFineRate || '50') : (facilityFineRate || '50')} × {example.days} ={" "}
                                            <span className="font-semibold text-red-600">₦{example.fine.toLocaleString()}</span>
                                            {example.fine >= Number(exampleRate || 0) && <span className="text-orange-600 text-xs ml-1">(capped at commission)</span>}
                                        </p>
                                        <p className="text-gray-800 font-semibold border-t border-gray-200 pt-1 mt-1">
                                            Net earning: ₦{Number(exampleRate || 0).toLocaleString()} − ₦{example.fine.toLocaleString()} ={" "}
                                            <span className="text-green-700">₦{example.net.toLocaleString()}</span>
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                <p className="text-xs font-semibold text-orange-800 mb-1">Both fines can apply simultaneously</p>
                                <p className="text-xs text-orange-700">
                                    If a shipment sits at facility for {facilityGracePeriodHours || 72}+ hours AND then takes {gracePeriodHours || 24}+ hours to deliver after OFD,
                                    both fines are deducted. Total capped at gross commission (minimum earning = ₦0).
                                </p>
                            </div>
                        </div>
                    </div>

                    {settings?.updated_at && (
                        <p className="text-xs text-gray-400 mt-6 text-center">
                            Last updated: {new Date(settings.updated_at).toLocaleString()}
                            {settings.last_updated_by_name && ` by ${settings.last_updated_by_name}`}
                        </p>
                    )}
                </div>
            </div>
        </>
    );
};

export default FineSettings;